import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/orizon-ui/Modal';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import { FormSection } from '../../../components/orizon-ui/FormField';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useConfirm } from '../../../components/orizon-ui/ConfirmDialog';
import {
  configScorta, formattaQta, percentualeScorta, formattaDataOra, formattaData,
} from '../../../components/gru/scorta';
import { ETICHETTE_MOVIMENTO_COLATA as ETICHETTE_MOVIMENTO } from '../../../components/colata/movimenti';
import OrdiniAperti from '../../../components/colata/OrdiniAperti';
import {
  colataArticolo, colataCampiReparto, colataLista, colataEsporta, colataElimina, colataImpostaAttivo, mapColataError,
} from '../../../lib/services/colataService';
import { useColataPermessi } from '../../../components/colata/ColataPermessi';
import CompatibilitaPanel from './CompatibilitaPanel';
import BarcodeCodice from '../../../components/colata/BarcodeCodice';
import FormArticolo from './FormArticolo';
import { CaricoModal, RettificaModal } from '../../../components/colata/AzioniGiacenza';

const Campo = ({ label, valore, mono = false }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">{label}</span>
    <span className={`text-[15px] font-bold text-foreground/90 break-words ${mono ? 'font-mono tracking-tight' : ''}`}>
      {valore === undefined || valore === null || valore === '' ? '---' : valore}
    </span>
  </div>
);

const valoreAttributo = (campo, valore) => {
  if (valore === undefined || valore === null) return null;
  if (campo.tipo === 'booleano') return valore ? 'Sì' : 'No';
  if (campo.tipo === 'selezione') return (campo.opzioni || []).find((o) => o.key === valore)?.label ?? String(valore);
  if (campo.tipo === 'data') return formattaData(valore);
  if (campo.tipo === 'numero' && campo.unita) return `${valore} ${campo.unita}`;
  return String(valore);
};

// movimenti mostrati all'apertura e a ogni "Mostra altri"
const MOV_INIZIALI = 5;
const MOV_PASSO = 20;

const BottoneFooter = ({ icona, label, onClick, tono = 'neutro', disabilitato = false }) => {
  const stili = {
    primario: 'bg-primary text-primary-foreground border-primary hover:opacity-90 shadow-lg shadow-primary/20',
    verde: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500 hover:text-white',
    neutro: 'bg-muted text-foreground border-border/40 hover:bg-muted/80',
    warn: 'bg-amber-500/10 text-amber-500 border-amber-500/30 hover:bg-amber-500 hover:text-white',
    pericolo: 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500 hover:text-white',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabilitato}
      className={`flex items-center gap-2 px-4 py-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all disabled:opacity-50 ${stili[tono]}`}
    >
      <Icon name={icona} size={14} />
      {label}
    </button>
  );
};

/**
 * Dettaglio di un articolo: giacenza, specifiche, compatibilità, ordini aperti e ultimi movimenti.
 * Da qui: Carico, Rettifica, Modifica, Disattiva/Riattiva, Elimina.
 */
const DettaglioArticolo = ({ articoloId, onClose, onAggiornato }) => {
  const { scrivere } = useColataPermessi(); // false = "Sola lettura": nessun comando di scrittura
  const toast = useToast();
  const confirm = useConfirm();

  const [articolo, setArticolo] = useState(null);
  const [campi, setCampi] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [totaleMov, setTotaleMov] = useState(0);
  const [caricandoMov, setCaricandoMov] = useState(false);
  const [ordiniModaleAperta, setOrdiniModaleAperta] = useState(false);
  const [caricamento, setCaricamento] = useState(true);
  const [sottoModale, setSottoModale] = useState(null); // 'modifica' | 'carico' | 'rettifica'
  const [occupato, setOccupato] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [compatAperta, setCompatAperta] = useState(false);

  const carica = useCallback(async () => {
    try {
      const a = await colataArticolo(articoloId);
      if (!a) {
        toast.error('Articolo non trovato (forse è stato eliminato).');
        onClose();
        return;
      }
      setArticolo(a);
      const [c, m] = await Promise.all([
        colataCampiReparto(a.reparto_id),
        colataLista('storico', {
          perPagina: MOV_INIZIALI, soloAttivi: false, filtri: { articolo_id: articoloId },
          sort: { key: 'created_at', direction: 'desc' },
        }),
      ]);
      setCampi(c);
      setMovimenti(m.righe);
      setTotaleMov(m.totale);
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setCaricamento(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoloId]);

  useEffect(() => {
    carica();
  }, [carica]);

  // PDF della scheda: usa i dati già caricati nel modale; carica solo la compatibilità (una query) e,
  // se per qualche motivo non fossero ancora presenti, gli ultimi movimenti. La libreria si carica al click.
  const scaricaPdf = async () => {
    if (generandoPdf || !articolo) return;
    setGenerandoPdf(true);
    try {
      const [{ generaPdfArticolo }, compat, ultimiMovimenti] = await Promise.all([
        import('../../../lib/colataPdfArticolo'),
        colataLista('compatibilita', {
          perPagina: 200, filtri: { articolo_id: articolo.id }, sort: { key: 'zona_codice', direction: 'asc' },
        }),
        // il PDF stampa tutti i movimenti dell'articolo, non solo quelli visibili nel modale
        colataEsporta('storico', {
          soloAttivi: false, filtri: { articolo_id: articolo.id }, sort: { key: 'created_at', direction: 'desc' },
        }).then((r) => r.righe),
      ]);
      await generaPdfArticolo({
        articolo,
        campi,
        compatibilita: compat.righe,
        compatibilitaTotale: compat.totale,
        movimenti: ultimiMovimenti,
      });
      toast.success('Scheda articolo scaricata (PDF).');
    } catch (e) {
      toast.error(`Impossibile generare il PDF: ${mapColataError(e).message}`);
    } finally {
      setGenerandoPdf(false);
    }
  };

  // carica i movimenti successivi a quelli già visibili
  const mostraAltri = async () => {
    if (caricandoMov) return;
    setCaricandoMov(true);
    try {
      const r = await colataLista('storico', {
        pagina: 0, perPagina: movimenti.length + MOV_PASSO, soloAttivi: false, filtri: { articolo_id: articoloId },
        sort: { key: 'created_at', direction: 'desc' },
      });
      setMovimenti(r.righe);
      setTotaleMov(r.totale);
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setCaricandoMov(false);
    }
  };

  const dopoAzione = async () => {
    setSottoModale(null);
    await carica();
    onAggiornato?.();
  };

  const cambiaAttivo = async (attivo) => {
    if (!attivo) {
      const ok = await confirm({
        titolo: 'Disattivare l\'articolo?',
        descrizione: `"${articolo.nome}" non sarà più selezionabile negli interventi. Giacenza e storico restano.`,
        confermaLabel: 'Disattiva',
        variante: 'primary',
      });
      if (!ok) return;
    }
    setOccupato(true);
    try {
      await colataImpostaAttivo('articoli', articolo.id, attivo);
      toast.success(attivo ? 'Articolo riattivato.' : 'Articolo disattivato.');
      await dopoAzione();
    } catch (e) {
      toast.error(mapColataError(e).message);
    } finally {
      setOccupato(false);
    }
  };

  const elimina = async () => {
    const ok = await confirm({
      titolo: 'Eliminare definitivamente?',
      descrizione: `"${articolo.nome}" verrà eliminato. Se ha movimenti o interventi non sarà possibile: in quel caso disattivalo.`,
      confermaLabel: 'Elimina',
    });
    if (!ok) return;

    setOccupato(true);
    try {
      await colataElimina('articoli', articolo.id);
      toast.success('Articolo eliminato.');
      onAggiornato?.();
      onClose();
    } catch (e) {
      const err = mapColataError(e);
      if (err.code === 'RECORD_IN_USO' && articolo.attivo) {
        const disattiva = await confirm({
          titolo: 'Articolo in uso',
          descrizione: `${err.message} Vuoi disattivare "${articolo.nome}"?`,
          confermaLabel: 'Disattiva',
          variante: 'primary',
        });
        if (disattiva) {
          try {
            await colataImpostaAttivo('articoli', articolo.id, false);
            toast.success('Articolo disattivato.');
            await dopoAzione();
          } catch (e2) {
            toast.error(mapColataError(e2).message);
          }
        }
      } else {
        toast.error(err.message);
      }
    } finally {
      setOccupato(false);
    }
  };

  if (caricamento || !articolo) {
    return (
      <Modal isOpen onClose={onClose} titolo="Caricamento..." etichetta="Dettagli articolo" larghezza="xl">
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">Caricamento...</span>
        </div>
      </Modal>
    );
  }

  const dec = Number(articolo.unita_decimali ?? 0);
  const scorta = configScorta(articolo.stato_scorta);
  const pct = percentualeScorta(articolo.qta, articolo.qta_min);
  const valoriTecnici = campi
    .map((c) => ({ campo: c, testo: valoreAttributo(c, articolo.attributi?.[c.key]) }))
    .filter((x) => x.testo !== null && (x.campo.attivo || x.testo !== null));

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        titolo={articolo.nome}
        etichetta="Dettagli articolo"
        larghezza="xl"
        intestazioneDestra={(
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={scaricaPdf}
              disabled={generandoPdf}
              title="Scarica la scheda articolo in PDF"
              aria-label="Scarica la scheda articolo in PDF"
              className="shrink-0 h-10 w-10 sm:w-auto sm:px-3.5 rounded-2xl bg-muted/50 hover:bg-primary/10 border border-border/40 hover:border-primary/30 text-muted-foreground hover:text-primary flex items-center justify-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-all shadow-sm disabled:opacity-60"
            >
              <Icon name={generandoPdf ? 'Loader2' : 'FileDown'} size={17} className={generandoPdf ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <BarcodeCodice valore={articolo.codice} className="w-[92px] sm:w-[130px]" />
          </div>
        )}
        bloccaChiusura={sottoModale !== null || occupato || compatAperta || ordiniModaleAperta}
        footer={!scrivere ? undefined : (
          <div className="flex flex-wrap items-center justify-end gap-2 w-full">
            <BottoneFooter icona="PackagePlus" label="Carico" tono="verde" onClick={() => setSottoModale('carico')} disabilitato={!articolo.attivo || occupato} />
            <BottoneFooter icona="Scale" label="Rettifica" tono="neutro" onClick={() => setSottoModale('rettifica')} disabilitato={!articolo.attivo || occupato} />
            <BottoneFooter icona="Pencil" label="Modifica" tono="primario" onClick={() => setSottoModale('modifica')} disabilitato={occupato} />
            {articolo.attivo ? (
              <BottoneFooter icona="PowerOff" label="Disattiva" tono="warn" onClick={() => cambiaAttivo(false)} disabilitato={occupato} />
            ) : (
              <BottoneFooter icona="Power" label="Riattiva" tono="verde" onClick={() => cambiaAttivo(true)} disabilitato={occupato} />
            )}
            <BottoneFooter icona="Trash2" label="Elimina" tono="pericolo" onClick={elimina} disabilitato={occupato} />
          </div>
        )}
      >
        {/* Badge stato + giacenza */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-5 rounded-3xl border flex flex-col gap-2 ${articolo.attivo ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-400/10 border-slate-400/30'}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-foreground/40">Stato articolo</span>
            <span className={`text-base font-black uppercase ${articolo.attivo ? 'text-emerald-500' : 'text-slate-400'}`}>
              {articolo.attivo ? 'Attivo' : 'Disattivato'}
            </span>
          </div>
          <div className={`p-5 rounded-3xl border flex flex-col gap-2 ${scorta.badge}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-foreground/40">Giacenza</span>
            <span className={`text-base font-black uppercase ${scorta.colore}`}>{scorta.label}</span>
          </div>
        </div>

        {/* Giacenza & scorte */}
        <FormSection titolo="Giacenza & Scorte" icona="Box">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Disponibile</span>
              <div className="flex items-baseline gap-2">
                <span className={`text-5xl font-black tabular-nums ${scorta.colore}`}>{formattaQta(articolo.qta, dec)}</span>
                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">{articolo.unita_label}</span>
              </div>
            </div>
            <Campo label="Scorta minima" valore={`${formattaQta(articolo.qta_min, dec)} ${articolo.unita_label}`} />
            <Campo label="Unità di misura" valore={articolo.unita_label} />
            <Campo
              label="In ordine"
              valore={articolo.n_ordini_aperti > 0
                ? `${formattaQta(articolo.qta_ordinata, dec)} ${articolo.unita_label} (${articolo.n_ordini_aperti})`
                : 'Nessun ordine'}
            />
          </div>
          <div className="space-y-3 pt-2">
            <div className="flex justify-between items-end">
              <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Salute Magazzino</span>
              <span className="text-xs font-black tabular-nums text-primary">{pct}%</span>
            </div>
            <div className="h-3 rounded-full bg-muted/60 overflow-hidden border border-border/20 p-0.5">
              <div className={`h-full rounded-full transition-all duration-1000 ease-out ${scorta.barra}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </FormSection>

        {/* Specifiche */}
        <FormSection titolo="Specifiche Tecniche" icona="Cpu">
          <div className="grid grid-cols-2 gap-y-8 gap-x-4">
            <Campo label="Codice" valore={articolo.codice} mono />
            <Campo label="Codice SAP" valore={articolo.codice_sap} mono />
            <Campo label="Codice fornitore" valore={articolo.codice_fornitore} mono />
            <Campo label="Ricambio" valore={articolo.reparto_label} />
            <Campo label="Zona" valore={articolo.zone_codici} mono />
            <Campo label="Impianto" valore={articolo.impianti_nomi} />
            <Campo
              label="Ubicazione"
              valore={articolo.ubicazione_codice ? `${articolo.magazzino_label ?? ''} · ${articolo.ubicazione_codice}` : null}
            />
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none">Codice a barre</span>
              <span className="text-emerald-500 text-[10px] font-black uppercase tracking-widest flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg w-fit">
                <Icon name="ShieldCheck" size={12} /> Codice valido
              </span>
            </div>
          </div>

          {articolo.descrizione && <Campo label="Descrizione" valore={articolo.descrizione} />}

          {valoriTecnici.length > 0 && (
            <div className="grid grid-cols-2 gap-y-8 gap-x-4 pt-2 border-t border-border/20">
              {valoriTecnici.map(({ campo, testo }) => (
                <Campo key={campo.key} label={`${campo.label}${campo.attivo ? '' : ' (disattivato)'}`} valore={testo} />
              ))}
            </div>
          )}
        </FormSection>

        {/* Compatibilità */}
        <FormSection titolo="Compatibilità con i componenti" icona="Link2">
          <CompatibilitaPanel articolo={articolo} onCambiato={onAggiornato} onModaleChange={setCompatAperta} />
        </FormSection>

        {/* Ordini aperti: registra ricezione / segna come ricevuto */}
        <OrdiniAperti articolo={articolo} onCambiato={dopoAzione} onModaleChange={setOrdiniModaleAperta} />

        {/* Ultimi movimenti */}
        <FormSection titolo="Ultimi movimenti" icona="History">
          {movimenti.length === 0 ? (
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40">Nessun movimento registrato.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {movimenti.map((m) => {
                const et = ETICHETTE_MOVIMENTO[m.tipo] ?? { label: m.tipo, tono: 'neutro' };
                const positivo = Number(m.qta_delta) > 0;
                return (
                  <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-muted/20 border border-border/30">
                    <div className="flex items-center gap-3 min-w-0">
                      <StatusPill tono={et.tono} label={et.label} />
                      {m.tipo === 'ordine' ? (
                        <span className="text-[13px] font-black tabular-nums text-foreground/70">
                          {formattaQta(m.intervento_qta, dec)} {articolo.unita_label}
                          <span className="ml-1.5 text-[10px] font-bold text-muted-foreground/60">in ordine · giacenza invariata</span>
                        </span>
                      ) : (
                        <>
                          <span className={`text-[14px] font-black tabular-nums ${positivo ? 'text-emerald-500' : 'text-red-500'}`}>
                            {positivo ? '+' : ''}{formattaQta(m.qta_delta, dec)}
                          </span>
                          <span className="text-[11px] font-bold text-muted-foreground tabular-nums">
                            → {formattaQta(m.qta_dopo, dec)} {articolo.unita_label}
                          </span>
                        </>
                      )}
                      {m.stornato && <StatusPill tono="warn" label="Stornato" />}
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] font-mono font-bold text-muted-foreground">{formattaDataOra(m.created_at)}</span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/50">{m.utente_nome}</span>
                    </div>
                    {m.motivo && <p className="w-full text-[12px] font-medium italic text-foreground/60">"{m.motivo}"</p>}
                  </div>
                );
              })}
              {movimenti.length < totaleMov && (
                <button
                  type="button"
                  onClick={mostraAltri}
                  disabled={caricandoMov}
                  className="self-center mt-1 flex items-center gap-2 px-4 py-2.5 rounded-2xl border border-border/40 bg-muted/30 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all disabled:opacity-50"
                >
                  <Icon name={caricandoMov ? 'Loader2' : 'ChevronDown'} size={14} className={caricandoMov ? 'animate-spin' : ''} />
                  Mostra altri ({totaleMov - movimenti.length})
                </button>
              )}
            </div>
          )}
        </FormSection>

        {/* Note */}
        <FormSection titolo="Note" icona="FileText">
          {articolo.note
            ? <p className="text-sm font-medium text-foreground/70 leading-relaxed italic">"{articolo.note}"</p>
            : <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 italic">Nessuna nota presente.</p>}
        </FormSection>
      </Modal>

      {sottoModale === 'modifica' && (
        <FormArticolo
          record={articolo}
          onClose={() => setSottoModale(null)}
          onSaved={dopoAzione}
        />
      )}
      {sottoModale === 'carico' && (
        <CaricoModal articolo={articolo} onClose={() => setSottoModale(null)} onDone={dopoAzione} />
      )}
      {sottoModale === 'rettifica' && (
        <RettificaModal articolo={articolo} onClose={() => setSottoModale(null)} onDone={dopoAzione} />
      )}
    </>
  );
};

export default DettaglioArticolo;
